import React, { useState, useEffect } from "react"
import PageTitleButton from "../../components/PageTitleButton"
import SplineChart from "../../components/EnergyDispensedChart"
import TotalVehicleChart from "../../components/TotalVehicleChart"
import ChargerStatusChart from "../../components/ChargerStatusChart"
import { Card, Col, Row, Table } from "react-bootstrap"
import { Link } from "react-router-dom"
import axiosProvider from "../../lib/AxiosProvider"
import { admin } from "../../config/apiEndpoints"
import { toast } from "react-toastify"
import Section1Skeleton from "../../skeleton/dashboard/Section1Skeleton"
import Section2Skeleton from "../../skeleton/dashboard/Section2Skeleton"
import Section3Skeleton from "../../skeleton/dashboard/Section3Skeleton"
import { useSelector } from "react-redux"

const Dashboard = () => {
  const [recordsCount, setRecordsCount] = useState({
    total_revenue: 0,
    total_bookings: 0,
    average_revenue: 0,
  })
  const [chargerStatus, setChargerStatus] = useState({
    available: 0,
    unavailable: 0,
    preparing: 0,
    charging: 0,
    faulted: 0,
  })
  const [vehicleData, setVehicleData] = useState({
    twoWheelerCount: 0,
    threeWheelerCount: 0,
    fourWheelerCount: 0,
  })
  const [topPerformingChargingStations, setTopPerformingChargingStations] =
    useState([])
  const [chartLoading, setChartLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const modulePermissionList = useSelector(
    (state) => state.modulePermissions.modulePermissionList
  )

  const fetchCountData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.dashboardCounts}`,
      })
      if (response?.data?.code === 200) {
        setRecordsCount({
          total_revenue: response?.data?.data?.total_revenue || 0,
          total_bookings: response?.data?.data?.total_bookings || 0,
          average_revenue: response?.data?.data?.average_revenue || 0,
        })
      }
    } catch (error) {
      toast.error(error)
    }
  }

  const fetchChargerStatusData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.dashboardChargerStatus}`,
      })
      if (response?.data?.code === 200) {
        setChargerStatus({
          available: response?.data?.data?.available || 0,
          unavailable: response?.data?.data?.unavailable || 0,
          preparing: response?.data?.data?.preparing || 0,
          charging: response?.data?.data?.charging || 0,
          faulted: response?.data?.data?.faulted || 0,
        })
        setChartLoading(false)
      }
    } catch (error) {
      toast.error(error)
    }
  }

  const fetchVehicleData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.dashboardVehicleStatus}`,
      })
      if (response?.data?.code === 200) {
        setVehicleData({
          twoWheelerCount: response?.data?.data?.twoWheelerCount || 0,
          threeWheelerCount: response?.data?.data?.threeWheelerCount || 0,
          fourWheelerCount: response?.data?.data?.fourWheelerCount || 0,
        })
      }
    } catch (error) {
      toast.error(error)
    }
  }

  const fetchTopPerformingChargingStation = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.dashboardTopPerformingChargingStation}`,
      })
      if (response?.data?.code === 200) {
        setTopPerformingChargingStations(response?.data?.data?.rows || [])
        setLoading(false)
      }
    } catch (error) {
      toast.error(error)
    }
  }

  useEffect(() => {
    fetchCountData()
    fetchVehicleData()
    fetchChargerStatusData()
    fetchTopPerformingChargingStation()
  }, [])

  return (
    <>
      <PageTitleButton PagetitleText={"Dashboard"} />
      {!loading ? (
        <div className="details_cards_wrapper mb-3">
          <div className="bg_sea_green details_card pe-none">
            <div className="card_titleicon">
              <h3 className="card_title">Total Revenue</h3>
              <span className="card_icon">
                <i className="icon_main money"></i>
              </span>
            </div>
            <p className="tls_numbers">{recordsCount?.total_revenue}</p>
          </div>
          <div className="bg_light_red details_card pe-none">
            <div className="card_titleicon">
              <h3 className="card_title">Total Booking</h3>
              <span className="card_icon">
                <i className="icon_main booking_timer"></i>
              </span>
            </div>
            <p className="tls_numbers">{recordsCount?.total_bookings}</p>
          </div>
          <div className="bg_light_green details_card pe-none">
            <div className="card_titleicon">
              <h3 className="card_title">Ave. Revenue / Station</h3>
              <span className="card_icon">
                <i className="icon_main chargin_stations"></i>
              </span>
            </div>
            <p className="tls_numbers">{recordsCount?.average_revenue}</p>
          </div>
        </div>
      ) : (
        <Section1Skeleton />
      )}
      {!chartLoading ? (
        <Row className="g-3 mb-3">
          <Col md="6">
            <Row className="g-3">
              <Col md="6">
                <div className="dshchart_card mb-3 mb-md-0">
                  <ChargerStatusChart chargerStatus={chargerStatus} />
                </div>
              </Col>
              <Col md="6">
                <div className="dshchart_card mb-3 mb-md-0">
                  <TotalVehicleChart vehicleData={vehicleData} />
                </div>
              </Col>
            </Row>
          </Col>
          <Col md="6">
            <div className="dshchart_card mb-3 mb-md-0">
              <SplineChart
                chartLoading={chartLoading}
                setChartLoading={setChartLoading}
              />
            </div>
          </Col>
        </Row>
      ) : (
        <Section2Skeleton />
      )}
      {!loading ? (
        <Card>
          <Card.Body>
            <h5 className="mb-4" style={{ fontSize: "18px" }}>
              Top Performing Charging Stations by Revenue
            </h5>
            <Table className="rounded mb-0 table-striped" responsive="md">
              <thead>
                <tr>
                  <th className="text-center">ID</th>
                  <th>Station Detail</th>
                  <th className="text-center">Consumption</th>
                  <th className="text-center">Bookings</th>
                  <th className="text-center">Total Revenue (₹)</th>
                </tr>
              </thead>
              <tbody>
                {topPerformingChargingStations?.length > 0 &&
                  topPerformingChargingStations?.map((item, index) => (
                    <tr key={index + 1}>
                      <td className="text-center">{item?.id}</td>
                      <td>
                        {modulePermissionList["station management"]?.some(
                          (perm) => perm.permission === "view"
                        ) ? (
                          <Link
                            to={`/charging-station-details/${item?.id}`}
                            className="text-decoration-none"
                          >
                            <div className="station_nmaddress">
                              <p className="station_nm">{item?.station_name}</p>
                              <span className="station_address">
                                {item?.address}
                              </span>
                            </div>
                          </Link>
                        ) : (
                          <div className="station_nmaddress">
                            <p className="station_nm">{item?.station_name}</p>
                            <span className="station_address">
                              {item?.address}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        {item?.total_consumption?.toFixed(2) || 0} W
                      </td>
                      <td className="text-center">
                        {item?.booking_count || 0}
                      </td>
                      <td className="text-center">
                        {item?.total_revenue?.toFixed(2) || 0} ₹
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <Section3Skeleton />
      )}
    </>
  )
}

export default Dashboard
